/* eslint-disable react/no-unused-state */
import _ from 'lodash';
import React from 'react';
import cn from 'classnames';

import { BBCommonProps } from 'types/story';
import { IframeTunnel } from 'common/utils/iframe-tunnel';
import { isLayerType } from 'common/utils/is-layer-type';
import { triggerFocus } from 'common/utils/helpers';
import { IFRAME_ACTIONS, COMPONENT_STATES, UNIFORM_PROPS } from 'common/constants';
import { CHILDREN_KEY } from 'client/components/common/BuildingBlocks/utils/common';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { FIELD_EDITABLE_ATTR } from 'client/components/common/BuildingBlocks/Fields/constants';
import css from './Text.scss';
import ContentEditable from './ContentEditable';

function transmitUpdatedValueToAdmin({ path, value, isStory }) {
	const iframeTunnel = new IframeTunnel({ id: 'Text' });
	iframeTunnel.messageTransmitter({
		target: IframeTunnel.targets.admin,
		action: IFRAME_ACTIONS[isStory ? 'UPDATE_STORY_ELEMENT_BY_PATH' : 'UPDATE_CARD_ELEMENT_BY_PATH'],
		payload: {
			path,
			value,
		},
	});
}

type State = {
	html: any;
	editing: boolean;
};

type Props = BBCommonProps;

class Text extends React.Component<Props, State> {
	static defaultProps = {
		editableModeProps: {},
		children: undefined,
		in: undefined,
	};

	editableRef = React.createRef<HTMLElement>();

	constructor(props, context) {
		super(props, context);

		this.state = {
			html: props.children,
			editing: false,
		};
	}

	static getDerivedStateFromProps({ children }: Props, state: State): Partial<State> | null {
		const isUpdatedChildrenFromProp = document.activeElement === document.body && children !== state.html;

		if (isUpdatedChildrenFromProp) {
			return { html: children, editing: false };
		}

		return null;
	}

	onChangeDebounced = _.debounce((getHtml: () => string) => {
		const html = getHtml();
		if (this.state.html !== html) this.save(html, true);
	}, 500);

	save = (html: string, isContinueEditing: boolean = false) => {
		const path = this.props.editableModeProps?.nodeProps?.['data-path'];
		const { currentMediaQuery } = this.props;

		if (!path) {
			console.warn("path isn't defined...");
			return;
		}

		// NOTE: children is always writes to defaultState, but for certain media query
		if (isLayerType(this.props).global) {
			transmitUpdatedValueToAdmin({
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: html,
				isStory: true,
			});
		} else {
			transmitUpdatedValueToAdmin({
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: html,
				isStory: false,
			});
		}

		this.setState({ editing: isContinueEditing, html });
	};

	updateSelectionRect = (p: { getHtml: () => string }) => {
		// trigger SelectionHint update to set current editable node sizes
		triggerFocus(this.editableRef.current);

		this.onChangeDebounced(p.getHtml);
	};

	enableEditing = () => {
		this.setState({ editing: true }, () => {
			this.editableRef.current?.focus();
		});
	};

	disableEditing = () => this.setState({ editing: false });

	renderEditable() {
		const { editableModeProps, uiConfig } = this.props;
		const { editing } = this.state;
		const nodeProps = _.merge({}, uiConfig.nodeProps, editableModeProps?.nodeProps);

		const props = {
			...nodeProps,
			...this.props.stateAttrs,
			...this.props.eventListeners,
			ref: this.editableRef,
			className: cn(css.text, css.editable, uiConfig.nodeProps.className),
		};

		if (editing) {
			props[FIELD_EDITABLE_ATTR] = ''; // prevent from dragging by <Points />
			props.onKeyUp = e => e.stopPropagation(); // prevent application keyboard shortcuts while editing text
			props.onKeyDown = e => e.stopPropagation(); // prevent application keyboard shortcuts while editing text
		} else {
			props.onDoubleClick = this.enableEditing;
			props.dangerouslySetInnerHTML = { __html: this.state.html };
		}

		return (
			<div {...props}>
				{editing ? (
					<ContentEditable
						html={this.state.html}
						onChange={this.updateSelectionRect}
						onComplete={this.disableEditing}
						editableRef={props.ref}
					/>
				) : null}
			</div>
		);
	}

	renderDefault() {
		const { nodeProps, componentProps } = this.props.uiConfig;
		const Tag = componentProps[UNIFORM_PROPS.tag] || 'p';

		return (
			<Tag
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				className={cn(css.text, nodeProps.className)}
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: this.state.html }}
				ref={this.props.containerRef}
			/>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

export default withCardTransitionContext(Text);
